import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { HERO_SLIDES, COMPANY } from "../data/site";

const SLIDE_MS = 6000;

const splitWords = (text) => text.split(" ");

const wordVariants = {
  hidden: { y: "100%", opacity: 0 },
  show: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: i * 0.05, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = ({ singlePage, slides: slidesProp, company: companyProp, cinematic }) => {
  const slides = slidesProp || HERO_SLIDES;
  const company = companyProp || COMPANY;
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);

 const next = useCallback(
  () => setIdx((i) => (i + 1) % slides.length),
  [slides.length]
);

const prev = useCallback(
  () => setIdx((i) => (i - 1 + slides.length) % slides.length),
  [slides.length]
);
  const go = useCallback((i) => setIdx(i), []);

  useEffect(() => {
    if (!playing) return;
    const t = setInterval(next, SLIDE_MS);
    return () => clearInterval(t);
  }, [playing, next, idx]);

  const slide = slides[idx];

  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-20 bg-white overflow-hidden"
    >
      <div className="absolute inset-0 dot-pattern opacity-50 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* TEXT */}
          <div className="lg:col-span-7 relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={`text-${idx}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
              >
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-navy-100 bg-navy-50">
                  <span className="relative flex w-1.5 h-1.5">
                    <span className="absolute inline-flex w-full h-full rounded-full bg-amber-500 opacity-75 ping-amber" />
                    <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-amber-500" />
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-navy-800 font-bold">
                    {slide.eyebrow}
                  </span>
                </div>

                <h1 className="mt-6 font-display text-[40px] leading-[1] sm:text-5xl md:text-6xl lg:text-[72px] font-bold text-navy-900 tracking-[-0.03em]">
                  <span className="block overflow-hidden">
                    <motion.span className="inline-block">
                      {splitWords(slide.titleTop).map((w, i) => (
                        <motion.span
                          key={`top-${idx}-${i}`}
                          custom={i}
                          variants={wordVariants}
                          initial="hidden"
                          animate="show"
                          className="inline-block mr-3"
                        >
                          {w}
                        </motion.span>
                      ))}
                    </motion.span>
                  </span>
                  <span className="block overflow-hidden">
                    <motion.span className="inline-block">
                      {splitWords(slide.titleAccent).map((w, i) => (
                        <motion.span
                          key={`acc-${idx}-${i}`}
                          custom={i + 2}
                          variants={wordVariants}
                          initial="hidden"
                          animate="show"
                          className="inline-block mr-3 text-amber-500"
                        >
                          {w}
                        </motion.span>
                      ))}
                    </motion.span>
                  </span>
                </h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="mt-6 max-w-xl text-base md:text-lg text-slate-600 leading-relaxed"
                >
                  {slide.subtitle}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55, duration: 0.6 }}
                  className="mt-8 flex flex-wrap items-center gap-3"
                >
                  {singlePage ? (
                    <a
                      href="#contact"
                      data-testid="hero-cta-quote"
                      className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-navy-900 hover:bg-navy-800 text-white font-semibold tracking-tight transition-all hover:gap-3"
                    >
                      Start a Conversation
                      <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform" />
                    </a>
                  ) : (
                    <Link
                      to="/contact"
                      data-testid="hero-cta-quote"
                      className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-navy-900 hover:bg-navy-800 text-white font-semibold tracking-tight transition-all hover:gap-3"
                    >
                      Start a Conversation
                      <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform" />
                    </Link>
                  )}
                  {singlePage ? (
                    <a
                      href="#capabilities"
                      data-testid="hero-cta-services"
                      className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 hover:border-amber-500 text-navy-900 font-semibold tracking-tight transition-all shadow-sm"
                    >
                      Explore Capabilities
                      <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform text-amber-600" />
                    </a>
                  ) : (
                    <Link
                      to="/services"
                      data-testid="hero-cta-services"
                      className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 hover:border-amber-500 text-navy-900 font-semibold tracking-tight transition-all shadow-sm"
                    >
                      Explore Capabilities
                      <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform text-amber-600" />
                    </Link>
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.6 }}
                  className="mt-8 flex flex-wrap items-center gap-2"
                >
                  {slide.chip.split("·").map((c, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 rounded-md bg-slate-100 border border-slate-200 text-navy-800 text-[11px] font-mono uppercase tracking-wider"
                    >
                      {c.trim()}
                    </span>
                  ))}
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* IMAGE */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl shadow-navy-900/15 bg-navy-50">
              <AnimatePresence mode="sync">
                <motion.div
                  key={`img-${idx}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0"
                >
                  <img
                    src={slide.image}
                    alt={slide.titleTop}
                    className={`absolute inset-0 w-full h-full object-cover ${cinematic ? "ken-burns" : ""}`}
                  />
                </motion.div>
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/35 via-transparent to-transparent" />

              <motion.div
                key={`card-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="absolute bottom-5 left-5 right-5 md:left-6 md:right-6"
              >
                <div className="bg-white/95 backdrop-blur-md rounded-2xl p-5 shadow-xl border border-white/60">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-amber-600 font-bold">
                    {company.tagline}
                  </div>
                  <div className="mt-2 text-navy-900 font-display text-lg font-bold leading-snug tracking-tight">
                    {slide.subtitle.split(".")[0]}.
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="absolute -top-4 -right-4 hidden md:flex w-20 h-20 rounded-2xl bg-amber-500 items-center justify-center shadow-lg shadow-amber-500/30 z-10">
              <div className="text-navy-900 font-display font-bold text-2xl tracking-tighter">
                0{idx + 1}
                <span className="text-navy-900/40">/0{slides.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Slider controls */}
        <div className="mt-10 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            {slides.map((_, i) => (
              <button
                key={i}
                data-testid={`hero-slide-dot-${i}`}
                onClick={() => go(i)}
                aria-label={`Go to slide ${i + 1}`}
                className="group"
              >
                <span
                  className={`block h-1 rounded-full transition-all duration-500 ${
                    i === idx ? "w-12 bg-navy-900" : "w-6 bg-slate-300 group-hover:bg-slate-400"
                  }`}
                />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="hero-slide-pause"
              onClick={() => setPlaying((p) => !p)}
              className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-navy-800 hover:border-amber-500 hover:text-amber-600 transition-all"
              aria-label={playing ? "Pause slider" : "Play slider"}
            >
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button
              data-testid="hero-slide-prev"
              onClick={prev}
              className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-navy-800 hover:border-amber-500 hover:text-amber-600 transition-all"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="hero-slide-next"
              onClick={next}
              className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-navy-800 hover:border-amber-500 hover:text-amber-600 transition-all"
              aria-label="Next slide"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
